// Utility to dynamically register web fonts from URLs at runtime

const injectedFamilies = new Set()

/**
 * Inject a font-face with specific weight and style
 * Supports fallback to CDN if proxy fails
 */
export function injectFontFace(family, url, weight = 'normal', style = 'normal') {
  if (!family || !url) return
  
  // Normalize URL - prefer proxy endpoint for CDN URLs to avoid CORS issues
  let normalizedUrl = url
  let fallbackUrl = null
  
  // If it's a direct CDN URL, convert to proxy endpoint with fallback
  if (url.startsWith('https://editor.blr1.cdn.digitaloceanspaces.com/fonts/')) {
    // Extract filename from CDN URL and use proxy endpoint
    const filename = url.split('/fonts/')[1]?.split('?')[0]
    if (filename) {
      normalizedUrl = `/api/v1/catalog/fonts/proxy/${encodeURIComponent(filename)}`
      fallbackUrl = url // Keep CDN URL as fallback
    }
  } else if (url.startsWith('http://') || url.startsWith('https://')) {
    // Other absolute URLs (keep as-is, might be other CDNs)
    normalizedUrl = url
  } else if (!url.startsWith('/') && !url.startsWith('data:')) {
    // Relative URL - ensure it starts with /
    normalizedUrl = url.startsWith('./') ? url.substring(1) : `/${url}`
  }
  
  const key = `${family}::${normalizedUrl}::${weight}::${style}`
  if (injectedFamilies.has(key)) return
  
  try {
    const styleEl = document.createElement('style')
    styleEl.type = 'text/css'
    styleEl.setAttribute('data-font', `${family}:${weight}:${style}`)
    
    // Convert weight to numeric value if needed
    const weightValue = normalizeWeight(weight)
    
    // Escape URL for CSS (handle special characters but preserve absolute URLs)
    const cssUrl = cssEscapeUrl(normalizedUrl)
    
    // Build src with fallback if available
    let src = `url('${cssUrl}') format('${detectFormat(normalizedUrl)}')`
    if (fallbackUrl) {
      // Add fallback CDN URL in case proxy fails
      const fallbackCssUrl = cssEscapeUrl(fallbackUrl)
      src += `, url('${fallbackCssUrl}') format('${detectFormat(fallbackUrl)}')`
    }
    
    styleEl.textContent = `@font-face { font-family: '${cssEscape(family)}'; src: ${src}; font-weight: ${weightValue}; font-style: ${style}; font-display: swap; }`
    document.head.appendChild(styleEl)
    injectedFamilies.add(key)
    
    // Best effort to prompt load
    if (document.fonts && document.fonts.load) {
      // Try loading with proxy URL first, fallback to CDN if needed
      document.fonts.load(`1rem '${family}'`).catch((err) => {
        if (fallbackUrl) {
          console.warn(`Font ${family} failed to load from proxy, will try CDN fallback:`, err)
        }
      })
    }
  } catch (error) {
    console.warn(`Failed to inject font ${family}:`, error)
  }
}

/**
 * Normalize weight string to numeric value
 */
function normalizeWeight(weight) {
  if (typeof weight === 'number') return weight
  const weightMap = {
    'thin': 100, '100': 100,
    'extralight': 200, 'ultralight': 200, '200': 200,
    'light': 300, '300': 300,
    'normal': 400, 'regular': 400, '400': 400,
    'medium': 500, '500': 500,
    'semibold': 600, 'demibold': 600, '600': 600,
    'bold': 700, '700': 700,
    'extrabold': 800, 'ultrabold': 800, '800': 800,
    'black': 900, 'heavy': 900, '900': 900,
  }
  return weightMap[String(weight).toLowerCase()] || 400
}

/**
 * Inject multiple fonts with their weight and style
 */
export function injectManyFonts(fonts = [], limit = 150) {
  let count = 0
  for (const f of fonts) {
    if (!f?.family || !f?.url) continue
    injectFontFace(
      f.family, 
      f.url, 
      f.weight || 'normal', 
      f.style || 'normal'
    )
    count += 1
    if (count >= limit) break
  }
}

/**
 * Inject all variants of a font family
 * @param {Array} allFonts - Array of all font entries from API
 * @param {String} family - Font family name
 * @param {Number} limit - Maximum number of variants to inject per family
 */
export function injectFontFamilyVariants(allFonts = [], family, limit = 50) {
  if (!family || !allFonts || allFonts.length === 0) return
  
  const variants = allFonts.filter(f => f.family === family)
  let count = 0
  for (const variant of variants) {
    if (!variant.url) continue
    injectFontFace(
      variant.family,
      variant.url,
      variant.weight || 'normal',
      variant.style || 'normal'
    )
    count += 1
    if (count >= limit) break
  }
}

function detectFormat(url) {
  const lower = String(url).toLowerCase()
  if (lower.endsWith('.otf')) return 'opentype'
  if (lower.endsWith('.ttf')) return 'truetype'
  return 'truetype'
}

function cssEscape(s) {
  return String(s).replace(/'/g, "\\'")
}

/**
 * Escape URL for use in CSS url() - preserve absolute URLs, escape quotes
 */
function cssEscapeUrl(url) {
  // For absolute URLs, just escape quotes
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url.replace(/'/g, "\\'").replace(/"/g, '\\"')
  }
  // For relative URLs, escape quotes and potentially spaces
  return url.replace(/'/g, "\\'").replace(/"/g, '\\"')
}


